import { useEffect, useReducer, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Single shared notification store so multiple mounted bells (e.g. desktop +
// mobile headers) share ONE polling loop and ONE toast stream — never double
// poll or double-notify.
const store = {
  items: [],
  unread: 0,
  subs: new Set(),
  seen: new Set(),
  seeded: false,
  timer: null,
  started: false,
};

function emit() {
  store.subs.forEach((fn) => fn());
}

async function fetchOnce() {
  try {
    // The bell shows ONLY unread — the full history lives on the
    // notifications page (/dashboard/notifications).
    const { data } = await api.get("/notifications?limit=30&unread_only=true");
    const list = data.notifications || [];
    store.items = list;
    store.unread = data.unread || 0;

    if (store.seeded) {
      const fresh = list.filter((n) => !store.seen.has(n.id) && !n.is_read);
      fresh
        .slice(0, 3)
        .reverse()
        // Notification toasts get the blue "info" accent.
        .forEach((n) => toast.info(n.title, { description: n.body || undefined }));
    }
    list.forEach((n) => store.seen.add(n.id));
    store.seeded = true;
    emit();
  } catch (_) {
    /* ignore */
  }
}

function start() {
  if (store.started) return;
  store.started = true;
  fetchOnce();
  store.timer = setInterval(fetchOnce, 20000);
  window.addEventListener("focus", fetchOnce);
  window.addEventListener("notif:refresh", fetchOnce);
}

function stop() {
  if (store.subs.size > 0) return;
  store.started = false;
  clearInterval(store.timer);
  store.timer = null;
  window.removeEventListener("focus", fetchOnce);
  window.removeEventListener("notif:refresh", fetchOnce);
}

export function useNotifications(enabled = true) {
  const [, force] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    if (!enabled) return undefined;
    store.subs.add(force);
    start();
    return () => {
      store.subs.delete(force);
      stop();
    };
  }, [enabled]);

  const markRead = useCallback(async (id) => {
    const it = store.items.find((n) => n.id === id);
    if (it && !it.is_read) {
      it.is_read = true;
      store.unread = Math.max(0, store.unread - 1);
      emit();
    }
    try {
      await api.post(`/notifications/${id}/read`);
    } catch (_) {}
  }, []);

  const markAllRead = useCallback(async () => {
    store.items = store.items.map((n) => ({ ...n, is_read: true }));
    store.unread = 0;
    emit();
    try {
      await api.post("/notifications/read-all");
    } catch (_) {}
  }, []);

  return {
    items: store.items,
    unread: store.unread,
    refetch: fetchOnce,
    markRead,
    markAllRead,
  };
}
